/**
 * Writing Assistant API client.
 * Handles text enhancement with LLM and system prompt management.
 */
import { api, getAuthToken } from "./client";

// =============================================================================
// Constants
// =============================================================================

/**
 * Default enhancement prompt used when no custom prompt is selected or entered.
 * Focuses on general text improvement: clarity, grammar, and formatting.
 */
export const DEFAULT_ENHANCEMENT_PROMPT = `Vous êtes un rédacteur professionnel spécialisé dans l'amélioration de textes.

QUESTION : {{question}}

TEXTE À AMÉLIORER :
{{answer}}

TÂCHE : Améliorer la clarté, la structure et la lisibilité du texte fourni tout en préservant l'ensemble du contenu factuel et en répondant à la question posée.

DIRECTIVES :
1. Corriger les erreurs de grammaire, d'orthographe et de ponctuation
2. Améliorer la structure des phrases et la fluidité du texte
3. Renforcer la clarté sans modifier le sens
4. Conserver le ton et le style d'origine
5. Préserver les termes techniques et les noms propres

SORTIE : Retourner uniquement le texte amélioré. Ne pas inclure d'explications.`;

/**
 * Available placeholders for prompt templates.
 * These are replaced dynamically when sending prompts to the AI model.
 */
export const PROMPT_PLACEHOLDERS = {
  question: "{{question}}",
  answer: "{{answer}}",
} as const;

// =============================================================================
// Types
// =============================================================================

/**
 * Available LLM models for enhancement.
 */
export type LLMModelChoice = "llama" | "openai";

/**
 * Request to enhance text using LLM.
 */
export interface EnhancementRequest {
  question: string;
  original_text: string;
  prompt_id?: string;
  /** Custom prompt text for single-use enhancement. Mutually exclusive with prompt_id. */
  prompt_text?: string;
  model?: LLMModelChoice;
}

/**
 * Response from non-streaming text enhancement.
 */
export interface EnhancementResponse {
  enhanced_text: string;
  prompt_id: string;
  processing_time_ms: number;
  token_count?: number;
}

/**
 * Streaming chunk from text enhancement.
 */
export interface EnhancementStreamChunk {
  content?: string;
  done: boolean;
  prompt_id?: string;
  processing_time_ms?: number;
  error?: string;
}

/**
 * System prompt for text enhancement.
 */
export interface SystemPrompt {
  id: string;
  name: string;
  prompt_text: string;
  description?: string;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  created_by_id?: string;
  updated_by_id?: string;
}

/**
 * Request to create a new system prompt.
 */
export interface SystemPromptCreate {
  name: string;
  prompt_text: string;
  description?: string;
  is_default?: boolean;
}

/**
 * Request to update a system prompt.
 */
export interface SystemPromptUpdate {
  name?: string;
  prompt_text?: string;
  description?: string;
  is_active?: boolean;
}

/**
 * Prompt version (historical snapshot).
 */
export interface PromptVersion {
  id: string;
  prompt_id: string;
  version_number: number;
  name: string;
  prompt_text: string;
  description?: string;
  changed_by_id: string;
  changed_at: string;
}

// =============================================================================
// Enhancement Endpoints
// =============================================================================

/**
 * Enhance text using LLM (non-streaming).
 * Returns the complete enhanced text once processing is done.
 */
export async function enhanceText(
  request: EnhancementRequest
): Promise<EnhancementResponse> {
  const response = await api.post("/writing-assistant/enhance", request);
  return response.data;
}

/**
 * Enhance text using LLM with streaming response.
 * Returns an async generator that yields chunks as they arrive.
 */
export async function* enhanceTextStream(
  request: EnhancementRequest
): AsyncGenerator<EnhancementStreamChunk, void, unknown> {
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(
    `${api.defaults.baseURL}/writing-assistant/enhance/stream`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error ${response.status}: ${errorText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No response body reader available");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Process complete lines (SSE format)
      const lines = buffer.split("\n");
      buffer = lines.pop() || ""; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.trim() === "") continue;

        if (line.startsWith("data: ")) {
          try {
            const data = line.slice(6); // Remove "data: " prefix
            const chunk: EnhancementStreamChunk = JSON.parse(data);
            yield chunk;

            if (chunk.done) {
              return;
            }
          } catch (error) {
            console.error("Error parsing SSE data:", error);
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// =============================================================================
// System Prompt Endpoints
// =============================================================================

/**
 * List all system prompts.
 * Admins can include inactive prompts.
 */
export async function listPrompts(
  includeInactive: boolean = false
): Promise<SystemPrompt[]> {
  const response = await api.get("/writing-assistant/prompts", {
    params: { include_inactive: includeInactive },
  });
  return response.data;
}

/**
 * Get a specific system prompt by ID.
 */
export async function getPrompt(promptId: string): Promise<SystemPrompt> {
  const response = await api.get(`/writing-assistant/prompts/${promptId}`);
  return response.data;
}

/**
 * Create a new system prompt (admin only).
 */
export async function createPrompt(
  data: SystemPromptCreate
): Promise<SystemPrompt> {
  const response = await api.post("/writing-assistant/prompts", data);
  return response.data;
}

/**
 * Update an existing system prompt (admin only).
 */
export async function updatePrompt(
  promptId: string,
  data: SystemPromptUpdate
): Promise<SystemPrompt> {
  const response = await api.put(
    `/writing-assistant/prompts/${promptId}`,
    data
  );
  return response.data;
}

/**
 * Delete (deactivate) a system prompt (admin only).
 */
export async function deletePrompt(promptId: string): Promise<void> {
  await api.delete(`/writing-assistant/prompts/${promptId}`);
}

/**
 * Set a prompt as the default (admin only).
 */
export async function setDefaultPrompt(
  promptId: string
): Promise<SystemPrompt> {
  const response = await api.post(
    `/writing-assistant/prompts/${promptId}/set-default`
  );
  return response.data;
}

// =============================================================================
// Version History Endpoints
// =============================================================================

/**
 * List version history for a prompt.
 */
export async function listPromptVersions(
  promptId: string,
  skip: number = 0,
  limit: number = 20
): Promise<PromptVersion[]> {
  const response = await api.get(
    `/writing-assistant/prompts/${promptId}/versions`,
    { params: { skip, limit } }
  );
  return response.data;
}

/**
 * Get a specific version of a prompt.
 */
export async function getPromptVersion(
  promptId: string,
  versionNumber: number
): Promise<PromptVersion> {
  const response = await api.get(
    `/writing-assistant/prompts/${promptId}/versions/${versionNumber}`
  );
  return response.data;
}

// =============================================================================
// API Class
// =============================================================================

/**
 * Writing Assistant API class for structured usage.
 */
export class WritingAssistantApi {
  /**
   * Enhance text (non-streaming).
   */
  async enhance(request: EnhancementRequest): Promise<EnhancementResponse> {
    return enhanceText(request);
  }

  /**
   * Enhance text with streaming.
   */
  async *enhanceStream(
    request: EnhancementRequest
  ): AsyncGenerator<EnhancementStreamChunk, void, unknown> {
    yield* enhanceTextStream(request);
  }

  /**
   * List all prompts.
   */
  async listPrompts(includeInactive: boolean = false): Promise<SystemPrompt[]> {
    return listPrompts(includeInactive);
  }

  /**
   * Get a prompt by ID.
   */
  async getPrompt(promptId: string): Promise<SystemPrompt> {
    return getPrompt(promptId);
  }

  /**
   * Create a new prompt.
   */
  async createPrompt(data: SystemPromptCreate): Promise<SystemPrompt> {
    return createPrompt(data);
  }

  /**
   * Update a prompt.
   */
  async updatePrompt(
    promptId: string,
    data: SystemPromptUpdate
  ): Promise<SystemPrompt> {
    return updatePrompt(promptId, data);
  }

  /**
   * Delete a prompt.
   */
  async deletePrompt(promptId: string): Promise<void> {
    return deletePrompt(promptId);
  }

  /**
   * Set a prompt as default.
   */
  async setDefaultPrompt(promptId: string): Promise<SystemPrompt> {
    return setDefaultPrompt(promptId);
  }

  /**
   * List version history for a prompt.
   */
  async listPromptVersions(
    promptId: string,
    skip: number = 0,
    limit: number = 20
  ): Promise<PromptVersion[]> {
    return listPromptVersions(promptId, skip, limit);
  }

  /**
   * Get a specific version of a prompt.
   */
  async getPromptVersion(
    promptId: string,
    versionNumber: number
  ): Promise<PromptVersion> {
    return getPromptVersion(promptId, versionNumber);
  }
}

// Export a default instance
export const writingAssistantApi = new WritingAssistantApi();
